import React, { useState, useMemo } from "react";
import { formatCurrency } from "../../utils/helpers";
import SiteDrillDown from "../analytics/SiteDrillDown";

const TOP_N = 10;

/**
 * Table layout for top spending sites (Report 2.0). Shows top 10 by total MRC.
 */
const Report20TopSitesTable = ({
  topSites = [],
  allSites = [],
  snapshotData = null,
  totalSpent = 0,
}) => {
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedCircuits, setSelectedCircuits] = useState([]);

  const displaySites = useMemo(() => {
    const fullList = allSites.length ? allSites : topSites;
    if (!fullList.length) return [];
    const sorted = [...fullList].sort(
      (a, b) => (parseFloat(b.totalMRC) || 0) - (parseFloat(a.totalMRC) || 0)
    );
    return sorted.slice(0, TOP_N);
  }, [allSites, topSites]);

  const topSharePct = useMemo(() => {
    const total = Number(totalSpent) || 0;
    if (total <= 0) return null;
    const topSum = displaySites.reduce((sum, site) => sum + (parseFloat(site.totalMRC) || 0), 0);
    return ((topSum / total) * 100).toFixed(0);
  }, [displaySites, totalSpent]);

  const handleSiteClick = (site) => {
    if (!snapshotData) return;
    const circuits = snapshotData.filter((record) => {
      const recordSite = [
        record.std_street_address || record.street_address,
        record.std_city || record.city,
        record.std_state || record.state,
        record.std_zip || record.zip,
      ]
        .filter(Boolean)
        .join(", ");
      return recordSite === site.site;
    });
    setSelectedCircuits(circuits);
    setSelectedSite(site);
  };

  if (!topSites?.length) {
    return (
      <p className="px-5 py-8 text-center text-[13px] text-[var(--muted,#6b6a66)]">
        No site data
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-2 px-5 pt-4 pb-3">
        <div>
          <h3 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]">
            Top 10 spending Locations
          </h3>
          <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">
            By total monthly recurring charges.
          </p>
        </div>
        {topSharePct != null ? (
          <p className="text-[12px] text-[var(--muted,#6b6a66)]">
            Top 10 = {topSharePct}% of total MRC
          </p>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
              <th className="px-5 pb-2 font-semibold">Location</th>
              <th className="px-3 pb-2 text-right font-semibold">Services</th>
              <th className="px-3 pb-2 text-right font-semibold whitespace-nowrap">Total MRC</th>
              <th className="px-5 pb-2 text-right font-semibold">Share</th>
            </tr>
          </thead>
          <tbody>
            {displaySites.map((site, index) => {
              const clickable = snapshotData != null;
              const mrc = parseFloat(site.totalMRC) || 0;
              const total = Number(totalSpent) || 0;
              const share = total > 0 ? ((mrc / total) * 100).toFixed(1) : "0.0";
              return (
                <tr
                  key={`${site.site}-${index}`}
                  onClick={() => clickable && handleSiteClick(site)}
                  className={`border-t border-[var(--line,#e4e2dc)] ${
                    clickable ? "cursor-pointer hover:bg-[var(--sunken,#eeece7)]" : ""
                  }`}
                >
                  <td className="px-5 py-2.5 text-[var(--ink,#1c1c1a)]">
                    <div className="flex items-start gap-2.5">
                      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--sunken,#eeece7)] text-[11px] font-semibold text-[var(--muted,#6b6a66)]">
                        {index + 1}
                      </span>
                      <span className="font-medium leading-snug">{site.site}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                    {site.recordCount ?? site.tier ?? "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-[var(--ink,#1c1c1a)] whitespace-nowrap">
                    {formatCurrency(site.totalMRC)}
                  </td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-[var(--muted,#6b6a66)]">
                    {share}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selectedSite && (
        <SiteDrillDown
          site={selectedSite}
          circuits={selectedCircuits}
          onClose={() => {
            setSelectedSite(null);
            setSelectedCircuits([]);
          }}
        />
      )}
    </>
  );
};

export default Report20TopSitesTable;
